import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, PolylineF, useJsApiLoader } from '@react-google-maps/api';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import AdvancedMarker from './AdvancedMarker';
import useMapColorScheme from '../../hooks/useMapColorScheme';

const DEFAULT_CENTER = { lat: 50.1109, lng: 8.6821 };
const MAP_OPTIONS = {
  fullscreenControl: false,
  streetViewControl: true,
  mapTypeControl: true,
  zoomControl: true,
  minZoom: 3,
  maxZoom: 19,
  mapId: '8bb9c199f544a76'
};

const PHASE_STYLE = {
  passenger_on_board: { color: '#1565c0', label: 'Fahrgaststrecke', weight: 6, opacity: 0.9 },
  to_pickup: { color: '#616161', label: 'Anfahrt', weight: 4, opacity: 0.75 },
  at_pickup: { color: '#00897b', label: 'Am Abholort', weight: 4, opacity: 0.8 },
  paused: { color: '#ed6c02', label: 'Pause', weight: 4, opacity: 0.8 },
  idle: { color: '#7b1fa2', label: 'Frei', weight: 4, opacity: 0.75 }
};

const splitTrack = (points, showApproach) => {
  const visible = points.filter(point => showApproach || !['to_pickup', 'at_pickup'].includes(point.phase));
  const paths = [];
  let current = [];
  let phase = null;
  visible.forEach(point => {
    const previous = current[current.length - 1];
    const gap = previous ? point.t - previous.t : 0;
    if (current.length && (point.phase !== phase || gap > 3 * 60 * 1000)) {
      if (current.length > 1) paths.push({ phase, points: current });
      current = [];
    }
    phase = point.phase || 'idle';
    current.push(point);
  });
  if (current.length > 1) paths.push({ phase, points: current });
  return paths;
};

const HistoricalTrackMap = ({
  points = [],
  selectedIndex = null,
  onSelectedIndexChange,
  showApproach = true,
  height = 460
}) => {
  const [map, setMap] = useState(null);
  const mapScheme = useMapColorScheme();
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: window.GOOGLE_MAPS_LIBRARIES,
    version: 'weekly'
  });
  const sortedPoints = useMemo(
    () => [...points].sort((a, b) => Number(a.t) - Number(b.t)),
    [points]
  );
  const visiblePoints = useMemo(
    () => sortedPoints.filter(point => showApproach || !['to_pickup', 'at_pickup'].includes(point.phase)),
    [showApproach, sortedPoints]
  );
  const paths = useMemo(() => splitTrack(sortedPoints, showApproach), [showApproach, sortedPoints]);
  const selectedPoint = selectedIndex === null ? null : sortedPoints[selectedIndex] || null;

  const fitTrack = useCallback((mapInstance) => {
    if (!mapInstance || !window.google || visiblePoints.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    visiblePoints.forEach(point => bounds.extend({ lat: point.lat, lng: point.lng }));
    mapInstance.fitBounds(bounds, 44);
    if (visiblePoints.length === 1) mapInstance.setZoom(16);
  }, [visiblePoints]);

  useEffect(() => {
    if (map) fitTrack(map);
  }, [fitTrack, map]);

  if (loadError) return <Alert severity="error">Karte konnte nicht geladen werden.</Alert>;
  if (!isLoaded) {
    return <Box sx={{ height, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
      <GoogleMap
        key={mapScheme.key}
        mapContainerStyle={{ width: '100%', height }}
        center={selectedPoint ? { lat: selectedPoint.lat, lng: selectedPoint.lng } : DEFAULT_CENTER}
        zoom={13}
        options={{ ...MAP_OPTIONS, colorScheme: mapScheme.colorScheme }}
        onLoad={(instance) => { setMap(instance); fitTrack(instance); }}
      >
        {paths.map((path, index) => {
          const style = PHASE_STYLE[path.phase] || PHASE_STYLE.idle;
          return (
            <PolylineF
              key={`${path.phase}-${index}`}
              path={path.points.map(point => ({ lat: point.lat, lng: point.lng }))}
              options={{
                strokeColor: style.color,
                strokeOpacity: style.opacity,
                strokeWeight: style.weight,
                clickable: true,
                icons: path.phase === 'to_pickup' ? [{
                  icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                  offset: '0',
                  repeat: '16px'
                }] : undefined
              }}
              onClick={() => {
                if (!onSelectedIndexChange) return;
                const point = path.points[Math.floor(path.points.length / 2)];
                onSelectedIndexChange(sortedPoints.indexOf(point));
              }}
            />
          );
        })}
        {visiblePoints.length > 0 && (
          <AdvancedMarker
            position={{ lat: visiblePoints[0].lat, lng: visiblePoints[0].lng }}
            title={`Start: ${new Date(visiblePoints[0].t).toLocaleString('de-DE')}`}
            status="pickup"
            label=""
            zIndex={900}
          />
        )}
        {visiblePoints.length > 1 && (
          <AdvancedMarker
            position={{ lat: visiblePoints[visiblePoints.length - 1].lat, lng: visiblePoints[visiblePoints.length - 1].lng }}
            title={`Ende: ${new Date(visiblePoints[visiblePoints.length - 1].t).toLocaleString('de-DE')}`}
            status="destination"
            label=""
            zIndex={900}
          />
        )}
        {selectedPoint && (
          <AdvancedMarker
            position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
            title={new Date(selectedPoint.t).toLocaleString('de-DE')}
            status="with_customer"
            label=""
            isRealtime
            zIndex={1000}
          />
        )}
      </GoogleMap>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ px: 1.5, py: 1, bgcolor: 'background.default' }}
      >
        {[...new Set(paths.map(path => path.phase))].map(phase => {
          const style = PHASE_STYLE[phase] || PHASE_STYLE.idle;
          return <Chip key={phase} size="small" label={style.label} sx={{ '&::before': {
            content: '""', width: 14, height: 3, bgcolor: style.color, ml: 1
          } }} />;
        })}
        {paths.length === 0 && (
          <Typography variant="caption" color="text.secondary">Keine zusammenhängende Strecke vorhanden</Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default HistoricalTrackMap;
